import { CHANGELOG, type ChangelogRelease } from './changelog';

export type ReleasePlatform = 'android' | 'ios' | 'web';
export type ReleaseCategory = 'features' | 'fixes' | 'security' | 'performance';
export type ChangeGroup = 'new' | 'fixes' | 'improvements' | 'issues';
export interface ReleaseEntry {
  id: string;
  text: string;
  summary?: string;
  group: ChangeGroup;
  categories: ReleaseCategory[];
}
export interface UpdateRelease extends ChangelogRelease {
  summary: string;
  platforms: ReleasePlatform[];
  entries: ReleaseEntry[];
  categories: ReleaseCategory[];
}

// Presentation metadata is authored here and included as additive API fields.
// Only explicitly documented platforms/categories are recorded. Older, unclassified
// releases remain readable/searchable under All platforms; absence is not a claim
// that a platform was unsupported or that there were no security improvements.
interface ReleasePresentation {
  summary?: string;
  /** Only confirmed, published issues; an empty list makes no claim of being bug-free. */
  knownIssues?: string[];
  highlightSummaries?: string[];
  fixSummaries?: string[];
  platforms?: ReleasePlatform[];
  improvements?: number[]; // indexes in the existing fixes list (not duplicate prose)
  securityHighlights?: number[];
  securityFixes?: number[];
  performanceFixes?: number[];
}
const PRESENTATION: Record<string, ReleasePresentation> = {
  '1.27.80': {
    summary:
      'Cherish replaces likes, Spread-It! reaches your followers, and Home and communities load page by page.',
    platforms: ['android', 'web'],
    improvements: [8, 9],
    securityFixes: [0, 1, 2, 10],
  },
  '1.27.70': {
    summary:
      'Two-factor to join in on videos, a card-based Account Settings, and Edit Profile as a full page.',
    platforms: ['web'],
    securityHighlights: [0],
  },
  '1.27.67': {
    summary: 'A calmer look: spaced details, no capitals, no bright outlines.',
    platforms: ['web'],
  },
  '1.27.66': {
    summary: 'Plainer punctuation across the site; no rule or policy meaning changed.',
    platforms: ['web'],
  },
  '1.27.65': {
    summary: 'Saved video captions now appear in the player and transcript.',
    platforms: ['web'],
  },
  '1.27.64': {
    summary: 'Two-factor secrets are stored encrypted.',
    platforms: ['web'],
    securityFixes: [0],
  },
  '1.27.63': {
    summary: 'Visitor counts no longer keep the part of an address after "?".',
    platforms: ['web'],
    securityFixes: [0],
  },
  '1.27.62': {
    summary: 'The Privacy Policy now matches what TruXSocial does.',
    platforms: ['web'],
  },
  '1.27.61': {
    summary: 'Changing an upload no longer spends your daily video allowance.',
    platforms: ['web'],
  },
  '1.27.60': {
    summary: 'Who sees your videos, captions and transcripts, clips, resume and a play queue.',
    platforms: ['web'],
    improvements: [0],
  },
  '1.27.51': {
    summary: 'The Flash side tabs reveal their names on hover.',
    platforms: ['web'],
    improvements: [0],
  },
  '1.27.50': {
    summary: 'Our own video player, a redesigned watch page, and Flash.',
    platforms: ['web'],
    improvements: [0, 1, 2],
  },
  '1.27.00': {
    summary: 'Post videos, watch them on their own page, and browse the new Videos page.',
    platforms: ['web'],
    improvements: [0, 1, 2],
  },
  '1.26.60': {
    summary: 'A connected web composer and a mobile feed that gives reading more room.',
    platforms: ['android', 'web'],
    improvements: [0, 1, 2],
  },
  '1.26.50M10': {
    summary: 'A clearer Android experience for reading, feedback and writing together.',
    platforms: ['android'],
    improvements: [0, 1, 2],
    highlightSummaries: [
      'All Posts, floating navigation and one connected side menu.',
      'Redesigned Feedback and a complete mobile Update Logs page.',
      'A new composer with Context, Cherish and flexible Mood tools.',
      'Live private collaboration and clearly attributed viewpoints.',
      'Saved Audience rings and links to real follow-up updates.',
      'Question tools and reviewed draft recovery.',
    ],
    fixSummaries: [
      'Your themes, gradients and softer controls carry across mobile.',
      'AnyDesk allows motion while screen-reader preferences stay respected.',
      'Private access is rechecked and unsent shared writing is preserved.',
    ],
  },
  '1.26.50': {
    summary: 'A clearer home for updates, feedback, settings and account shortcuts.',
    platforms: ['web'],
    improvements: [0, 1, 2],
    highlightSummaries: [
      'A complete Update Logs page with rotating release banners and filters.',
      'A redesigned Feedback board with customizable supporting panels.',
      'Softer shared controls and one organized account menu.',
      'One consistent Settings header with a collapsible section rail.',
    ],
    fixSummaries: [
      'Feedback menus now follow the active light or dark theme.',
      'Cards and filters use softer states and fit narrower screens.',
      'The account menu aligns cleanly with the bottom profile box.',
    ],
  },
  '1.26.00': {
    summary: 'Find your people. Share what you know. Build a network worth coming back to.',
    platforms: ['android', 'web'],
    improvements: [0, 1, 2],
    securityFixes: [0, 1],
    highlightSummaries: [
      'Signal Mixes and finite Catch Up sessions for your Home feed.',
      'Starter Packs to discover people, communities and Mixes.',
      'Community welcome guides, scheduled AMAs and text events.',
      'Shared learning collections and guides with contributor consent.',
      'Co-authored posts, creator channels and a creator workspace.',
      'Video playlists, live recaps, saved searches and quieter alerts.',
      'Direct links across the website and Android app.',
    ],
    fixSummaries: [
      'Unavailable tools disappear safely without deleting your data.',
      'Private access and contributor consent are checked before sharing.',
      'Older apps keep safe notification fallbacks; new links open directly.',
    ],
  },
  '1.25.60': {
    summary: 'Your look, better conversations, and a more personal Android experience.',
    platforms: ['android'],
    improvements: [0, 2],
    securityFixes: [1],
  },
  '1.25.50': {
    summary: 'More room for conversations, personal layouts, and looks that feel like you.',
    platforms: ['web'],
    improvements: [0, 1, 2],
  },
  '1.25.00': { platforms: ['android', 'web'], improvements: [3] },
  '1.24.80': { platforms: ['android', 'web'] },
  '1.24.70': { platforms: ['web'] },
  '1.24.60': { platforms: ['web'], performanceFixes: [0] },
  '1.22.00': { platforms: ['web'], securityHighlights: [0, 1, 2] },
};

export const PLATFORM_LABELS: Record<ReleasePlatform, string> = {
  android: 'Android',
  ios: 'iOS',
  web: 'Web',
};
export const CATEGORY_LABELS: Record<ReleaseCategory, string> = {
  features: 'Features',
  fixes: 'Fixes',
  security: 'Security',
  performance: 'Performance',
};

/** Future releases still render immediately, even before their optional metadata is curated. */
export function presentRelease(release: ChangelogRelease): UpdateRelease {
  const meta = PRESENTATION[release.version];
  const entries: ReleaseEntry[] = [
    ...release.highlights.map(
      (text, index): ReleaseEntry => ({
        id: `${release.version}-new-${index}`,
        text,
        group: 'new',
        summary: meta?.highlightSummaries?.[index],
        categories: meta?.securityHighlights?.includes(index)
          ? ['features', 'security']
          : ['features'],
      }),
    ),
    ...release.fixes.map(
      (text, index): ReleaseEntry => ({
        id: `${release.version}-fix-${index}`,
        text,
        summary: meta?.fixSummaries?.[index],
        group: meta?.improvements?.includes(index) ? 'improvements' : 'fixes',
        categories: [
          ...(meta?.improvements?.includes(index) ? [] : ['fixes' as const]),
          ...(meta?.securityFixes?.includes(index) ? ['security' as const] : []),
          ...(meta?.performanceFixes?.includes(index) ? ['performance' as const] : []),
        ],
      }),
    ),
    ...(meta?.knownIssues ?? []).map(
      (text, index): ReleaseEntry => ({
        id: `${release.version}-issue-${index}`,
        text,
        group: 'issues',
        categories: [],
      }),
    ),
  ];
  return {
    ...release,
    entries,
    summary: meta?.summary ?? release.title,
    platforms: meta?.platforms ?? [],
    categories: [...new Set(entries.flatMap((entry) => entry.categories))],
  };
}

export const UPDATE_RELEASES = CHANGELOG.map(presentRelease);
export interface ReleaseFilters {
  search: string;
  version: string;
  platform: ReleasePlatform | 'all';
  category: ReleaseCategory | 'all';
}
export const EMPTY_RELEASE_FILTERS: ReleaseFilters = {
  search: '',
  version: 'all',
  platform: 'all',
  category: 'all',
};
export function filterReleases(releases: UpdateRelease[], filters: ReleaseFilters) {
  const query = filters.search.trim().toLocaleLowerCase();
  return releases.filter(
    (release) =>
      (filters.version === 'all' || filters.version === release.version) &&
      (filters.platform === 'all' || release.platforms.includes(filters.platform)) &&
      (filters.category === 'all' || release.categories.includes(filters.category)) &&
      (!query ||
        [
          release.version,
          release.title,
          release.summary,
          release.date,
          ...release.entries.map((entry) => entry.text),
        ]
          .join(' ')
          .toLocaleLowerCase()
          .includes(query)),
  );
}

export function releaseStats(releases: UpdateRelease[], year: string) {
  const selected =
    year === 'all' ? releases : releases.filter((release) => release.date.startsWith(`${year}-`));
  return {
    total: selected.length,
    improvements: selected.reduce(
      (count, release) =>
        count + release.entries.filter((entry) => entry.group === 'improvements').length,
      0,
    ),
    fixes: selected.reduce(
      (count, release) => count + release.entries.filter((entry) => entry.group === 'fixes').length,
      0,
    ),
    security: selected.filter((release) => release.categories.includes('security')).length,
  };
}

// Availability is derived from releases that explicitly record each platform.
// “Latest listed” describes the release record, not the member's installed build.
export function latestPlatformRelease(releases: UpdateRelease[], platform: ReleasePlatform) {
  return releases.find((release) => release.platforms.includes(platform));
}

export function releaseDate(date: string) {
  return new Intl.DateTimeFormat('en', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
}
